import { cast, getEnv, types as t } from 'mst-effect';
import Router from 'next/router';
import { Concept } from 'api/grpc/resources';
import { mapOr } from 'utils/fp';
import { withRunInAction } from 'utils/withRunInAction';
import type { InputsManagerStore } from 'modules/InputsManager/store';
import { HighlightColor, SrcType, SearchBarState, SmartSearchState } from 'modules/InputsManager/types';
import { convertDashesToSpaces } from 'modules/InputsManager/helpers';

export interface SearchToken {
  text: string;
  color: HighlightColor;
  conceptSrcType: SrcType;
  conceptId: string;
}

export const SearchInputBarStore = t
  .compose(
    withRunInAction(),
    t.model('SearchInputStore', {
      /** Search text */
      searchText: t.string,

      searchBarInteractionState: t.enumeration('SearchBarState', [SearchBarState.Editing, SearchBarState.Tokenized]),

      /** Public concepts use as ranks, derived from search text */
      publicConcepts: t.optional(t.array(t.frozen<Concept>()), []),

      /** App concepts used as ranks, derived from search text */
      appConcepts: t.optional(t.array(t.frozen<Concept>()), []),

      /** Text that is display with color, derived from search text */
      searchTokens: t.optional(t.array(t.frozen<SearchToken>()), []),
      isRankSearchInProcess: t.optional(t.boolean, false),
      isRefetchInputs: t.optional(t.boolean, false),
    }),
  )
  .views((self) => ({
    get env() {
      return getEnv<CF.MSTEnvironment>(self);
    },
    get isEditing() {
      return self.searchBarInteractionState === SearchBarState.Editing;
    },
    get smartSearchState(): SmartSearchState {
      const serializeConcept = (c: Concept) => {
        return {
          appId: c.getAppId(),
          userId: c.getUserId(),
          id: c.getId(),
          name: c.getName(),
        };
      };
      return {
        searchText: self.searchText,
        searchBarState: self.searchBarInteractionState,
        publicConcepts: self.publicConcepts.map(serializeConcept),
        appConcepts: self.appConcepts.map(serializeConcept),
        searchTokens: self.searchTokens.toJSON(),
      };
    },
  }))
  .actions((self) => ({
    setSearchBarInteractionState(state: SearchBarState) {
      self.searchBarInteractionState = state;
    },
  }))
  .actions((self) => ({
    /** Updates Search Text */
    setSearchText(text: string) {
      self.searchText = text;
    },

    /** add concepts which are not current app concepts */
    addPublicConcept(concept: Concept) {
      const isAlreadyAddedConcept = self.publicConcepts.find((c) => c.getId() === concept.getId());
      if (!isAlreadyAddedConcept) {
        self.publicConcepts.push(concept);
      }
    },

    addAppConcept(concept: Concept) {
      const isAlreadyAddedConcept = self.appConcepts.find((c) => c.getId() === concept.getId());
      if (!isAlreadyAddedConcept) {
        self.appConcepts.push(concept);
      }
    },

    setSearchTokens(searchTokens: SearchToken[]) {
      self.searchTokens = cast(searchTokens);
    },

    /** reset search token */
    clearSearchToken() {
      self.searchTokens.replace([]);
      self.publicConcepts.replace([]);
      self.appConcepts.replace([]);
      self.setSearchBarInteractionState(SearchBarState.Editing);
    },
  }))
  .actions((self) => {
    async function _searchForPublicConcept(tokenizeText: string, defaultWorkflowId?: string) {
      const { env } = self;
      const { router } = Router;
      const { userOrOrgId, appId } = router?.query as { userOrOrgId: string; appId: string };
      const call = {
        userOrOrgId,
        appId,
        workflowId: defaultWorkflowId,
        page: 1,
        perPage: 128,
        name: tokenizeText,
        includeMainAppConcepts: true,
        fuzzy: false,
      };

      const result = await env.grpc.postConceptsSearches(call);
      const concepts = mapOr(result, (resp) => resp.getConceptsList(), []);
      const matchPublicConcept = concepts.find((concept) => concept.getAppId() !== appId);
      if (matchPublicConcept) {
        self.addPublicConcept(matchPublicConcept);
      }

      return matchPublicConcept;
    }

    async function _processWordAsPublicConcept(word: string, defaultWorkflowId?: string) {
      const conceptName = convertDashesToSpaces(word.slice(1));
      const publicConcept = await _searchForPublicConcept(conceptName, defaultWorkflowId);
      if (publicConcept) {
        self.addPublicConcept(publicConcept);
        return _publicConceptToken(word, publicConcept.getId());
      }
      return undefined;
    }

    function _processWordAsAppConcept(word: string) {
      const conceptName = convertDashesToSpaces(word.slice(1));
      const parent: InputsManagerStore = self as InputsManagerStore;
      const trainedConceptMatch = parent.trainedConcepts.find((concept: Concept) => {
        return concept.getName() === conceptName;
      });

      if (trainedConceptMatch) {
        self.addAppConcept(trainedConceptMatch);
        return _appConceptToken(word, trainedConceptMatch.getId());
      }
      return undefined;
    }

    function _publicConceptToken(text: string, conceptId: string) {
      return {
        text,
        color: HighlightColor.Blue,
        conceptSrcType: SrcType.Public,
        conceptId,
      };
    }

    function _appConceptToken(text: string, conceptId: string) {
      return {
        text,
        color: HighlightColor.Blue,
        conceptSrcType: SrcType.App,
        conceptId,
      };
    }

    function _unIdentifiedToken(text: string) {
      return { text, color: HighlightColor.Grey, conceptSrcType: SrcType.Unidentified, conceptId: text };
    }

    function _smartSearchIncludesConcept(concept: Concept) {
      return self.searchTokens.findIndex((token) => token.conceptId === concept.getId()) > -1;
    }

    return {
      /** convert search text to token */
      async tokenizeSearchText(defaultWorkflowId?: string) {
        const processedWords = self.searchText.split(' ').map(async (word) => {
          if (word.startsWith('#')) {
            const appConceptToken = _processWordAsAppConcept(word);
            if (appConceptToken) {
              return appConceptToken;
            }
            const publicConceptToken = await _processWordAsPublicConcept(word, defaultWorkflowId);
            if (publicConceptToken) {
              return publicConceptToken;
            }
          }
          return _unIdentifiedToken(word);
        });

        const tokens = await Promise.all(processedWords);
        self.setSearchTokens(tokens);
        self.setSearchBarInteractionState(SearchBarState.Tokenized);
      },

      /**
       * If the concept doesn't already exist in the current search, add it.
       * @param concept The Concept to add
       */
      addConceptToSmartSearch(concept: Concept) {
        if (_smartSearchIncludesConcept(concept)) {
          return;
        }
        if (concept.getAppId() === 'main' && concept.getUserId() === 'clarifai') {
          self.searchTokens.push(_publicConceptToken(`#${concept.getName()}`, concept.getId()));
          self.addPublicConcept(concept);
        } else {
          self.searchTokens.push(_appConceptToken(`#${concept.getName()}`, concept.getId()));
          self.addAppConcept(concept);
        }
        const newSearchText = self.searchText.length > 0 ? `${self.searchText} #${concept.getName()}` : `#${concept.getName()}`;
        self.setSearchText(newSearchText);
        self.setSearchBarInteractionState(SearchBarState.Tokenized);
      },

      smartSearchIncludesConcept(concept: Concept) {
        return _smartSearchIncludesConcept(concept);
      },
    };
  });
